import { toRaw } from 'vue';
import { ExcelProcessor } from './ExcelProcessor';
import { ExcelProcessorValidation } from './ExcelProcessorValidation';
import { XMLTaskParser, P6_TASK_KEY, parseP6XmlDocument } from './XMLTaskParser';
import TaskScheduleValidator from './TaskScheduleValidator';
import { measure } from './perf';
import { yieldToMain } from './mainThreadScheduler';
import { runProcessingTask } from '@/workers/workerClient';
import { useScheduleStore } from '@/stores/schedule';
import { useTaskStore } from '@/stores/task';
import { useResourceStore } from '@/stores/resource';

/**
 * Utility class for processing spreadsheet files (Excel/XML)
 */
export class SpreadsheetProcessor {
  constructor(selectedSchedule, selectedTimeRangeTemplate = null, existingTasks = []) {
    this.scheduleStore = useScheduleStore();
    this.taskStore = useTaskStore();
    this.resourceStore = useResourceStore();
    this.selectedSchedule = selectedSchedule;
    this.selectedTimeRangeTemplate = selectedTimeRangeTemplate;
    this.existingTasks = existingTasks;
  }

  /**
   * Process an Excel file and extract tasks
   * @param {Uint8Array} data - Excel file data
   * @returns {Promise<Object>} Processing result with nestedTasks, displayTasks, taskSetsPayload, changes, isValid, errors
   */
  async processExcelFile(data, onProgress) {
    // Workbook parsing (XLSX.read + sheet_to_json) is a single synchronous
    // block that cannot be time-sliced, so it runs in the processing worker
    // (main-thread fallback where Worker is unavailable).
    if (onProgress) onProgress({ stage: 'reading-workbook' });
    await yieldToMain();
    const { rows: worksheet, hasTimeRangeSheet, timeRangeId, taskSetMode } =
      await measure('xlsx-read', () => runProcessingTask('parseXlsx', { data }));

    // Resolve the extracted selectedTimeRangeId (store lookup stays on the
    // main thread)
    if (hasTimeRangeSheet) {
      const scheduleTemplates = this.scheduleStore.getScheduleTemplates;
      const selectedScheduleTemplate = scheduleTemplates.find(
        template => template.id === this.selectedSchedule.scheduleTemplateId
      );

      this.selectedTimeRangeTemplate = selectedScheduleTemplate?.timeRangeTemplates?.find(
        schedule => schedule.id === timeRangeId
      );
    }

    const excelProcessor = new ExcelProcessor(
      this.selectedSchedule,
      this.selectedTimeRangeTemplate,
      taskSetMode,
      this.existingTasks
    );
    return measure('excel-build-nested-tasks', () => excelProcessor.buildNestedTasks(worksheet, onProgress));
  }

  /**
   * Process an XML file and extract tasks
   * @param {string|Document} data - XML file text, or an already parsed
   *   Document (e.g. kept from the calendar pre-scan at file selection) so the
   *   expensive DOM parse is not repeated
   * @returns {Promise<Object>} Processing result with nestedTasks, displayTasks, taskSetsPayload, changes, isValid, errors
   */
  async processXMLFile(data, onProgress) {
    let xmlDoc = data;
    if (typeof data === 'string') {
      // DOMParser.parseFromString is atomic — it cannot be chunked, and on very
      // large exports it blocks for seconds. Yield first so the progress UI is
      // on screen before the unavoidable block starts.
      if (onProgress) onProgress({ stage: 'parsing-xml' });
      await yieldToMain();
      xmlDoc = measure('xml-dom-parse', () => parseP6XmlDocument(data));
    }
    const xmlTaskParser = new XMLTaskParser(
      this.selectedSchedule,
      this.selectedTimeRangeTemplate
    );
    const { tasks, calendars, taskCalendarLinks, relationshipSummary, prunedEmptyWbsCount, durationSummary } =
      await measure('xml-task-parse', () => xmlTaskParser.parse(xmlDoc, { onProgress }));

    // P6 XML skips the spreadsheet row/WBS checks, but the commit-blocking
    // start-date rule still applies: a tracking range with no StartDate in
    // the XML would otherwise be committed as startDate: null. The calendar
    // check is NOT run here — un-imported P6 calendars are re-linked after
    // the calendar import dialog, so it would false-positive at parse time.
    const validation = new ExcelProcessorValidation();
    validation.validateTimeRangeStartDates(tasks);
    const errors = validation.getErrors();

    return {
      nestedTasks: tasks,
      displayTasks: tasks,
      taskSetsPayload: [],
      changes: [],
      isValid: errors.length === 0,
      errors,
      groupedErrors: validation.getGroupedErrors(),
      p6Calendars: calendars,
      // task id -> P6 calendar ObjectId (Map). Used to re-link tasks after the
      // P6 calendars are created/updated. Never sent in the commit payload.
      taskCalendarLinks,
      // Every P6 task is stamped with this lock key; the commit must present
      // it in `keys` so re-imports may update the previously locked tasks.
      commitKeys: [P6_TASK_KEY],
      // What the P6 <Relationship> import kept and dropped (finish-to-start
      // zero-lag edges are applied as prerequisiteTaskIds); shown on the
      // review step. Never sent in the commit payload.
      relationshipSummary,
      // WBS nodes dropped because they contain no activities (and so carry no
      // dates); shown on the review step.
      prunedEmptyWbsCount,
      // Committed working-time durations checked against P6's own activity
      // durations; shown on the review step.
      durationSummary
    };
  }

  /**
   * Process a file (Excel or XML)
   * @param {File} file - File object to process
   * @param {Object} [options]
   * @param {Function} [options.onProgress] - Called with { stage, done, total }
   *   as processing advances (done/total omitted for indeterminate stages)
   * @param {Document} [options.xmlDoc] - For .xml files: the file's already
   *   parsed Document (from the calendar pre-scan at selection time). When
   *   given, the file is neither re-read nor re-parsed.
   * @returns {Promise<Object>} Processing result with nestedTasks, displayTasks, taskSetsPayload, changes, validation info
   */
  async processFile(file, { onProgress, xmlDoc } = {}) {
    let result;
    if (file.name.endsWith('.xlsx')) {
      const data = new Uint8Array(await readFileContents(file, 'arrayBuffer'));
      result = await this.processExcelFile(data, onProgress);
    } else if (file.name.endsWith('.xml')) {
      const data = xmlDoc || await readFileContents(file, 'text');
      result = await this.processXMLFile(data, onProgress);
    } else {
      throw new Error('Unsupported file type. Please upload .xlsx or .xml file.');
    }

    // Check if the spreadsheet is valid
    if (!result.isValid) {
      return {
        ...result,
        validationErrors: result.errors,
        groupedErrors: result.groupedErrors || []
      };
    }

    // Validate the new nested tasks
    // toRaw: the diff reads every field of every stored task; going
    // through the store's reactive proxy costs a trap per property
    // access, which adds up on tens of thousands of tasks.
    const tasks = toRaw(this.taskStore.getTasks);
    const locations = toRaw(this.resourceStore.getLocations) || [];
    const validator = new TaskScheduleValidator(tasks, result.displayTasks, locations);
    const validationResult = await measure('schedule-diff-validate', () => validator.validate({ onProgress }));

    return {
      nestedTasks: result.nestedTasks,
      displayTasks: validationResult.tasks,
      taskSetsPayload: result.taskSetsPayload,
      changes: validationResult.changes,
      isValid: true,
      validationErrors: [],
      groupedErrors: [],
      // P6-only metadata; empty/undefined for Excel imports.
      p6Calendars: result.p6Calendars || [],
      taskCalendarLinks: result.taskCalendarLinks || null,
      commitKeys: result.commitKeys || [],
      relationshipSummary: result.relationshipSummary || null,
      prunedEmptyWbsCount: result.prunedEmptyWbsCount || 0,
      durationSummary: result.durationSummary || null
    };
  }
}

/**
 * Read a File as text or ArrayBuffer.
 * @param {File} file
 * @param {'text'|'arrayBuffer'} as
 * @returns {Promise<string|ArrayBuffer>}
 */
function readFileContents(file, as) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(error);
    if (as === 'arrayBuffer') reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
  });
}
